import { Extension } from '@tiptap/core';

import { fontStackFor } from '../fontStack';
import { SIGNATURE_NODE_NAME } from './signatureNode';
import { cssStringLiteral, markerTemplateParts } from '../listMarkerCss';

/** One CSS declaration for an attribute's value, or nothing when it has none to contribute. */
type StyleFor = (value: unknown) => string | undefined;

/**
 * An attribute the editor must carry through untouched, and show.
 *
 * `data-*` is what `parseHTML` reads back, so the round trip is unaffected by the styling.
 * `style` is what makes the attribute VISIBLE: an alignment or an indent the author cannot see
 * is one they cannot check against the Word document in front of them, and this document has
 * thirteen right-aligned paragraphs and thirty indented ones. TipTap's `mergeAttributes` merges
 * `style` declarations property by property, so each attribute contributes its own.
 */
const passthrough = (
	name: string,
	parse: (raw: string) => unknown = (raw) => raw,
	styleFor?: StyleFor
) => ({
	default: null,
	parseHTML: (element: HTMLElement) => {
		const raw = element.getAttribute(`data-${name.toLowerCase()}`);
		return raw === null ? null : parse(raw);
	},
	renderHTML: (attributes: Record<string, unknown>) => {
		const value = attributes[name];
		if (value === null || value === undefined) {
			return {};
		}
		const declaration = styleFor?.(value);
		return {
			[`data-${name.toLowerCase()}`]: String(value),
			...(declaration === undefined ? {} : { style: declaration }),
		};
	},
});

/** An object attribute, carried whole. A malformed value parses to nothing; the save validates. */
const json = (name: string) => ({
	default: null,
	parseHTML: (element: HTMLElement) => {
		const raw = element.getAttribute(`data-${name.toLowerCase()}`);
		if (raw === null) {
			return null;
		}
		try {
			return JSON.parse(raw) as unknown;
		} catch {
			return null;
		}
	},
	renderHTML: (attributes: Record<string, unknown>) => {
		const value = attributes[name];
		return value === null || value === undefined
			? {}
			: { [`data-${name.toLowerCase()}`]: JSON.stringify(value) };
	},
});

const numeric = (name: string, styleFor?: StyleFor) =>
	passthrough(name, (raw) => Number(raw), styleFor);
const boolean = (name: string, styleFor?: StyleFor) =>
	passthrough(name, (raw) => raw === 'true', styleFor);

/** Millimetres straight through: the model's unit is the one CSS can state outright. */
const mm =
	(property: string): StyleFor =>
	(value) =>
		`${property}: ${String(value)}mm`;

/**
 * Declares every attribute the flow model uses on the nodes TipTap already provides.
 *
 * Not optional decoration: ProseMirror drops any attribute its schema does not declare, so an
 * undeclared `indentLeftMm` is silently lost the moment the document is loaded into the editor
 * — the author would see their indents disappear on open and have no way to know why. The
 * round-trip test exists to keep this list honest.
 */
export const FlowAttributes = Extension.create({
	name: 'flowAttributes',

	addGlobalAttributes() {
		return [
			{
				types: ['paragraph'],
				attributes: {
					align: passthrough(
						'align',
						(raw) => raw,
						(value) => `text-align: ${String(value)}`
					),
					indentLeftMm: numeric('indentLeftMm', mm('margin-left')),
					indentRightMm: numeric('indentRightMm', mm('margin-right')),
					// Negative hangs the first line, which is what puts a clause number in its
					// own gutter. CSS `text-indent` takes a negative length for exactly that.
					firstLineIndentMm: numeric('firstLineIndentMm', mm('text-indent')),
					spaceBeforeMm: numeric('spaceBeforeMm', mm('margin-top')),
					spaceAfterMm: numeric('spaceAfterMm', mm('margin-bottom')),
					lineHeight: numeric('lineHeight', (value) => `line-height: ${String(value)}`),
					fontSizePt: numeric('fontSizePt', (value) => `font-size: ${String(value)}pt`),
					fontFamily: passthrough(
						'fontFamily',
						(raw) => raw,
						(value) => `font-family: ${fontStackFor(String(value))}`
					),
					pageBreakBefore: boolean('pageBreakBefore'),
				},
			},
			{
				types: ['orderedList'],
				attributes: {
					// Held on the list, not the item, so the renderer numbers from position and
					// an item inserted at the top renumbers the rest. `data-numberformat` is what
					// the stylesheet selects the counter style on.
					numberFormat: passthrough('numberFormat'),
					// Split into what sits either side of the counter, so `(%1)` shows as `(a)`
					// rather than the browser's default `a.`.
					markerTemplate: passthrough(
						'markerTemplate',
						(raw) => raw,
						(value) => {
							const parts = markerTemplateParts(String(value));
							return [
								`--ds-list-prefix: ${cssStringLiteral(parts.prefix)}`,
								`--ds-list-suffix: ${cssStringLiteral(parts.suffix)}`,
								...(parts.nestedSeparator === undefined
									? []
									: [
											`--ds-list-separator: ${cssStringLiteral(parts.nestedSeparator)}`,
										]),
							].join('; ');
						}
					),
				},
			},
			{
				types: ['orderedList', 'bulletList'],
				attributes: {
					// Word's counter for this list, which the renderer counts across the whole
					// document. A bullet level counts too, so it carries one as well.
					numbering: json('numbering'),
					// Where Word puts the list, which the renderer honours over its own
					// nesting step.
					indentLeftMm: numeric('indentLeftMm'),
					hangingMm: numeric('hangingMm'),
					// Where the renderer puts the text after the marker, as Word does.
					tabStopsMm: json('tabStopsMm'),
					markerSuffix: passthrough('markerSuffix'),
				},
			},
			{
				types: ['bulletList'],
				attributes: {
					// The glyph the reader already mapped out of Word's private-use area, so a
					// Symbol-font bullet shows as a bullet instead of a missing-glyph box.
					bullet: passthrough(
						'bullet',
						(raw) => raw,
						(value) => `--ds-list-marker: ${cssStringLiteral(String(value))}`
					),
				},
			},
			{
				types: ['table'],
				attributes: {
					columnWidthsMm: passthrough('columnWidthsMm', (raw) =>
						raw.split(',').map((part) => Number(part))
					),
					// How the renderer sizes and places the columns, as Word does. The editor's
					// own table already fits its columns to their content.
					layout: passthrough('layout'),
					widthMm: numeric('widthMm'),
					align: passthrough('align'),
					indentMm: numeric('indentMm'),
					cellMarginsMm: json('cellMarginsMm'),
					// Fed to the cells as custom properties, because a border belongs to the
					// cell edges and there is no CSS route from a table's own border to them.
					borderWidthMm: numeric('borderWidthMm', mm('--ds-cell-border-width')),
					borderColor: passthrough(
						'borderColor',
						(raw) => raw,
						(value) => `--ds-cell-border-color: ${String(value)}`
					),
					pageBreakBefore: boolean('pageBreakBefore'),
				},
			},
			{
				types: ['tableRow'],
				attributes: {
					isHeader: boolean('isHeader'),
					minHeightMm: numeric('minHeightMm', mm('height')),
				},
			},
			{
				types: ['tableCell', 'tableHeader'],
				attributes: {
					bg: passthrough(
						'bg',
						(raw) => raw,
						(value) => `background-color: ${String(value)}`
					),
					valign: passthrough(
						'valign',
						(raw) => raw,
						(value) => `vertical-align: ${String(value)}`
					),
				},
			},
			{
				types: ['image'],
				attributes: {
					widthMm: numeric('widthMm', mm('width')),
					heightMm: numeric('heightMm', mm('height')),
					pageBreakBefore: boolean('pageBreakBefore'),
				},
			},
			{
				// The rule that decides whether this content appears. Declared on every node type
				// the model allows one on — ProseMirror DROPS an attribute its schema does not
				// declare, so an omission here is not a missing feature: the marking silently
				// never lands, the document never changes, and the rule is discarded at save as
				// unused. `flowSchema.spec` pins this list against the model for that reason.
				// `tableCell` is deliberately absent: the model forbids a conditional cell
				// because the column grid resolves in index lockstep.
				types: [
					'paragraph',
					'listItem',
					'bulletList',
					'orderedList',
					'table',
					'tableRow',
					'image',
					SIGNATURE_NODE_NAME,
				],
				attributes: {
					// No `styleFor`: the gutter and label are drawn off `data-rule` by the
					// stylesheet, which can key on the rule id.
					// Not carried onto the paragraph Enter creates: the next clause the author types is
					// not conditional just because the one before it was.
					rule: { ...passthrough('rule'), keepOnSplit: false },
				},
			},
		];
	},
});
